import * as React from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Text,
  Grid,
  GridItem,
  IconButton,
  useColorModeValue
} from "@chakra-ui/react";

interface CalendarProps {
  value?: Date;
  onSelect?: (date: Date) => void;
  showOutsideDays?: boolean;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  position?: "absolute" | "relative";
}

function Calendar({
  value,
  onSelect,
  showOutsideDays = true,
  disabled = false,
  minDate,
  maxDate,
  position = "relative",
  ...props
}: CalendarProps) {
  const [currentDate, setCurrentDate] = React.useState(value || new Date());

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "white");
  const selectedBg = useColorModeValue("black", "blue.400");
  const todayBg = useColorModeValue("gray.100", "gray.700");
  const outsideDayColor = useColorModeValue("gray.400", "gray.500");

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const firstDayOfWeek = firstDay.getDay();

    const days = [];

    // Dias do mês anterior (para preencher o início da semana)
    if (showOutsideDays && firstDayOfWeek > 0) {
      const prevMonth = new Date(year, month - 1, 0);
      const daysInPrevMonth = prevMonth.getDate();
      for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        days.push({
          date: new Date(year, month - 1, daysInPrevMonth - i),
          isOutside: true
        });
      }
    }

    // Dias do mês atual
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isOutside: false
      });
    }

    // Dias do próximo mês (para completar as 6 semanas)
    if (showOutsideDays) {
      const totalSlotsNeeded = 42; // 6 semanas * 7 dias
      const remainingDays = totalSlotsNeeded - days.length;

      if (remainingDays > 0) {
        for (let i = 1; i <= remainingDays; i++) {
          days.push({
            date: new Date(year, month + 1, i),
            isOutside: true
          });
        }
      }
    }

    return days;
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date) => {
    return value && date.toDateString() === value.toDateString();
  };

  const isDisabled = (date: Date) => {
    if (disabled) return true;

    // Comparar apenas datas (sem horário) para permitir seleção de hoje
    if (minDate) {
      const compareDate = new Date(date);
      compareDate.setHours(0, 0, 0, 0);
      const compareMinDate = new Date(minDate);
      compareMinDate.setHours(0, 0, 0, 0);
      if (compareDate < compareMinDate) return true;
    }

    if (maxDate) {
      const compareDate = new Date(date);
      compareDate.setHours(23, 59, 59, 999);
      const compareMaxDate = new Date(maxDate);
      compareMaxDate.setHours(23, 59, 59, 999);
      if (compareDate > compareMaxDate) return true;
    }

    return false;
  };

  const handleDateSelect = (date: Date) => {
    if (!isDisabled(date)) {
      onSelect?.(date);
    }
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const monthNames = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
  ];

  const weekDays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

  const days = getDaysInMonth(currentDate);

  return (
    <Box
      bg={bgColor}
      border="1px"
      borderColor={borderColor}
      borderRadius="md"
      p={3}
      position={position}
      zIndex={1000}
      {...props}
    >
      {/* Cabeçalho */}
      <Flex justify="space-between" align="center" mb={4}>
        <IconButton
          aria-label="Mês anterior"
          icon={<ChevronLeftIcon />}
          size="sm"
          variant="ghost"
          onClick={goToPreviousMonth}
          isDisabled={disabled}
        />
        <Text fontWeight="medium" fontSize="sm">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </Text>
        <IconButton
          aria-label="Próximo mês"
          icon={<ChevronRightIcon />}
          size="sm"
          variant="ghost"
          onClick={goToNextMonth}
          isDisabled={disabled}
        />
      </Flex>

      {/* Dias da semana */}
      <Grid templateColumns="repeat(7, 1fr)" gap={1} mb={2}>
        {weekDays.map((day) => (
          <GridItem key={day}>
            <Text
              fontSize="xs"
              fontWeight="normal"
              color="gray.500"
              textAlign="center"
              w="36px"
            >
              {day}
            </Text>
          </GridItem>
        ))}
      </Grid>

      {/* Dias do calendário */}
      <Grid templateColumns="repeat(7, 1fr)" gap={1}>
        {days.map((day, index) => (
          <GridItem key={`${day.date.getFullYear()}-${day.date.getMonth()}-${day.date.getDate()}-${day.isOutside}`}>
            <Button
              size="sm"
              variant="ghost"
              w="36px"
              h="36px"
              p={0}
              fontSize="sm"
              fontWeight="normal"
              onClick={() => handleDateSelect(day.date)}
              isDisabled={isDisabled(day.date)}
              bg={
                isSelected(day.date)
                  ? selectedBg
                  : isToday(day.date)
                    ? todayBg
                    : "transparent"
              }
              color={
                isSelected(day.date)
                  ? "white"
                  : day.isOutside
                    ? outsideDayColor
                    : textColor
              }
              _hover={
                !isDisabled(day.date)
                  ? {
                    bg: isSelected(day.date) ? selectedBg : "gray.100",
                  }
                  : {}
              }
              _focus={{
                boxShadow: "outline",
              }}
            >
              {day.date.getDate()}
            </Button>
          </GridItem>
        ))}
      </Grid>
    </Box>
  );
}

Calendar.displayName = "Calendar";

export { Calendar };
